import React from "react";
import { QueryRenderer } from "react-relay";

import moment from "moment";
import { graphql } from "relay-runtime";
import { Search, type SearchProps, type SearchResultData } from "semantic-ui-react";
import type { SearchResultProps } from "semantic-ui-react/dist/commonjs/modules/Search/SearchResult";

import { DueDate } from "~/common";
import type { CreateOrImportProps } from "~/integrations/actionItemIntegration/actionItemLinkingModal";
import { TrelloActionItemIntegrationLinkWidget, trelloSpecificDataNodeTypename } from "~/integrations/trello/Widget";
import useDebouncedValue from "~/lib/hooks/useDebouncedValue";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { toast } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";

import type { ImportExistingTicketPaneMutation } from "~/__generated__/ImportExistingTicketPaneMutation.graphql";
import type {
	ImportExistingTicketPaneTrelloQuery,
	ImportExistingTicketPaneTrelloQuery$data,
} from "~/__generated__/ImportExistingTicketPaneTrelloQuery.graphql";

import commonStyles from "~/integrations/actionItemIntegration/css/styles.module.css";
import styles from "~/integrations/trello/css/styles.module.css";

const searchQuery = graphql`
	query ImportExistingTicketPaneTrelloQuery($search: String!) {
		viewer {
			integrations {
				trello {
					searchCards(query: $search) {
						id
						externalId
						member {
							id
							fullName
							image
						}
						name
						due
						board {
							id
							externalId
							name
						}
						list {
							id
							externalId
							name
						}
					}
				}
			}
		}
	}
`;

const importExternalTicket = graphql`
	mutation ImportExistingTicketPaneTrelloMutation(
		$blockKey: String!
		$noteId: ID!
		$initialText: String!
		$destinationInfo: JSON!
	) {
		loadActionItemFromIntegration(
			input: {
				blockKey: $blockKey
				noteId: $noteId
				initialText: $initialText
				destinationInfo: $destinationInfo
				integrationProvider: TRELLO
			}
		) {
			clientMutationId
		}
	}
`;

type TrelloSearchCard = NonNullable<
	NonNullable<
		NonNullable<ImportExistingTicketPaneTrelloQuery$data["viewer"]["integrations"]["trello"]>["searchCards"]
	>[0]
>;

function importActionItem(
	blockKey: string,
	noteId: string,
	destinationInfo: { [val: string]: string },
	initialText: string,
) {
	return commitMutationPromise<ImportExistingTicketPaneMutation>(environment, {
		mutation: importExternalTicket,
		variables: { blockKey, noteId, destinationInfo, initialText },
	}).catch(() => {
		toast.error("There was an error importing this card. Please try again.");
	});
}

function ResultRenderer(props: SearchResultProps) {
	const card = props.data as TrelloSearchCard;
	return (
		<div className={styles.searchResultRow}>
			<span>{`[${card.board.name}] ${card.name}`}</span>
		</div>
	);
}

export function ImportExistingTicketPane({
	setCanSubmit,
	registerSubmit,
	blockKey,
	noteId,
	initialText,
}: CreateOrImportProps) {
	const [search, setSearch] = React.useState("");
	const debouncedSearch = useDebouncedValue(search, 300);
	const [selectedCard, setSelectedCard] = React.useState<TrelloSearchCard | undefined>();

	const submit = React.useCallback(async () => {
		if (!selectedCard) {
			throw new Error("Invalid state, submit should not be available if card is null");
		}
		const info = {
			/* eslint-disable camelcase */
			card_id: selectedCard.externalId || "",
			list_id: selectedCard.list.externalId,
			board_id: selectedCard.board.externalId,
			board_name: selectedCard.board.name,
			/* eslint-enable camelcase */
		};
		return importActionItem(blockKey, noteId, info, initialText).then(() => true);
	}, [blockKey, noteId, initialText, selectedCard]);

	React.useEffect(() => {
		registerSubmit(submit);
	}, [registerSubmit, submit]);

	const onSearchChange = React.useCallback(
		(event: React.MouseEvent<HTMLElement>, data: SearchProps) => {
			const value = data.value;
			if (value !== undefined) {
				setSearch(value);
				if (selectedCard) {
					setSelectedCard(undefined);
					setCanSubmit(false);
				}
			}
		},
		[selectedCard, setCanSubmit],
	);

	const onResultSelect = React.useCallback(
		(event: React.MouseEvent<HTMLElement>, data: SearchResultData) => {
			setSearch("");
			setSelectedCard(data.result.data);
			setCanSubmit(true);
		},
		[setCanSubmit],
	);

	const renderSearch = React.useCallback(
		({ props }: { props: ImportExistingTicketPaneTrelloQuery$data | null }) => {
			return (
				<Search
					autoFocus
					icon={<i aria-hidden="true" className="dropdown icon" />}
					loading={!props && search !== ""}
					placeholder="Search for a card name"
					className={styles.search}
					results={props?.viewer?.integrations?.trello?.searchCards?.map(card => ({
						title: card.name,
						key: card.id,
						data: card,
					}))}
					onSearchChange={onSearchChange}
					onResultSelect={onResultSelect}
					resultRenderer={ResultRenderer}
					showNoResults={false}
					value={search}
				/>
			);
		},
		[onResultSelect, onSearchChange, search],
	);

	return (
		<div className={styles.rowContainer}>
			<label>
				<div className={styles.label}>Trello Card</div>

				<QueryRenderer<ImportExistingTicketPaneTrelloQuery>
					environment={environment}
					query={searchQuery}
					variables={{ search: debouncedSearch }}
					render={renderSearch}
				/>
			</label>

			{selectedCard ? (
				<label>
					<div className={styles.label}>Preview</div>

					<div className={commonStyles.preview}>
						<div className={commonStyles.row}>
							<div className={commonStyles.items}>
								<div className={commonStyles.item}>
									<div className={commonStyles.actionCheckbox} />
								</div>

								<div className={commonStyles.item}>{selectedCard.name}</div>
							</div>

							{selectedCard.member || selectedCard.due ? (
								<div className={commonStyles.items}>
									{selectedCard.due && (
										<div className={commonStyles.item}>
											<DueDate as="span" styled short date={moment(selectedCard.due).format("YYYY-MM-DD")} />
										</div>
									)}

									{selectedCard.member && (
										<div className={commonStyles.item}>
											<Avatar user={selectedCard.member} link={false} width="24px" />
										</div>
									)}
								</div>
							) : null}
						</div>

						<div className={commonStyles.pillWrapper}>
							<TrelloActionItemIntegrationLinkWidget
								actionItemLink={{
									integrationData: {
										__typename: trelloSpecificDataNodeTypename,
										externalCardId: selectedCard.externalId,
										boardName: selectedCard.board.name,
									},
									syncStatus: "DONE_SYNCING_EXTERNALLY",
								}}
							/>
						</div>
					</div>
				</label>
			) : null}
		</div>
	);
}
